import { useState } from 'react';
import { pipe } from 'fp-ts/function';
import { getOrElse, some, chain, none } from 'fp-ts/Option';
import { useLocation } from 'react-router';
import { BUTTON_SIZES } from 'ui/button/constants';
import { getUrlParams } from 'utils/urlParams';
import { NBAData, Suggestion } from 'modules/ai/nba/types/nbaQuery';
import { MessageMedium } from 'modules/domains/campaign';
import { Modal, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { AiLight24 } from 'ui/icons/aurora/24px/AiLight24';
import { useCreateCloneCampaignAndTemplate } from 'modules/ai/nba/hooks';
import { CampaignsContainer } from 'modules/ai/nba/components/campaigns/CampaignsContainer';
import { useNBAContext } from 'modules/ai/nba/context/NBAContext';
import { updateAICampaignPayload } from 'modules/ai/nba/utils/transformCampaign';
import { NBACreationLoading } from 'modules/ai/nba/components/NBACreationLoading';
import {
  StyledGradientHeader,
  StyledModalHeader,
  StyledModalBodyWrapper,
  StyledReengagementModalHeader,
  StyledNextBestAction,
  StyledReengagementModalFooter,
  StyledCampaignCreateButton,
} from 'modules/ai/nba/components/NBAReengagementModal.styles';
import { NBACreationErrorModal } from 'modules/ai/nba/components/NBACreationErrorModal';
import { RecommendedContentList } from './recommendedContents/RecommendedContentList';

type Props = {
  isModalOpen: boolean;
  messageMedium: MessageMedium;
  onClose: () => void;
};

export const nbaReengageIds = {
  nbaReengagementModalId: 'nba-re-engagement-modal',
  nbaReengagementFooter: 'nba-re-engagement-footer',
  nbaReengagementCreateBtn: 're-engagement-create-campaign-btn',
};

export const NBAReengagementModal = ({ isModalOpen, messageMedium, onClose }: Props) => {
  const { search } = useLocation();
  const { campaignId } = getUrlParams(search);
  const [selectedValue, setSelectedValue] = useState<string>('0');
  const { nbaData, templateLocaleData } = useNBAContext();

  const suggestions = pipe(
    nbaData,
    chain((nbaInner: NBAData) =>
      nbaInner.suggestions && nbaInner.suggestions.length > 0 ? some(nbaInner.suggestions) : none,
    ),
    getOrElse(() => [] as Suggestion[]),
  );

  const { cloneCampaign, isError, isLoading, resetState } = useCreateCloneCampaignAndTemplate(
    Number(campaignId),
    updateAICampaignPayload,
    templateLocaleData,
    suggestions[Number(selectedValue)],
  );

  const modalBody = isLoading ? (
    <NBACreationLoading />
  ) : (
    <StyledModalBodyWrapper>
      <CampaignsContainer messageMedium={messageMedium} />
      <RecommendedContentList
        suggestions={suggestions}
        onSelectValue={setSelectedValue}
        selectedValue={selectedValue}
        messageMedium={messageMedium}
      />
    </StyledModalBodyWrapper>
  );

  const isCreateButtonDisabled = isLoading;

  return (
    <Modal
      data-test={nbaReengageIds.nbaReengagementModalId}
      id={nbaReengageIds.nbaReengagementModalId}
      isModalOpen={isModalOpen}
      onClose={onClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <StyledModalHeader id="nba-re-engagment-header" onClose={onClose}>
        <StyledReengagementModalHeader>
          <div>
            <StyledNextBestAction>
              <AiLight24 /> <span>Next Best Action</span>
            </StyledNextBestAction>
          </div>
          <StyledGradientHeader>Retarget unengaged users</StyledGradientHeader>
        </StyledReengagementModalHeader>
      </StyledModalHeader>
      {modalBody}
      <StyledReengagementModalFooter
        hasCancel={false}
        id={nbaReengageIds.nbaReengagementFooter}
        data-test={nbaReengageIds.nbaReengagementFooter}
        onClose={onClose}
      >
        <StyledCampaignCreateButton
          size={BUTTON_SIZES.LARGE}
          isDisabled={isCreateButtonDisabled}
          id={nbaReengageIds.nbaReengagementCreateBtn}
          data-test={nbaReengageIds.nbaReengagementCreateBtn}
          onClick={cloneCampaign}
        >
          Create campaign draft
        </StyledCampaignCreateButton>
      </StyledReengagementModalFooter>
      {isError && <NBACreationErrorModal onClose={resetState} onRetry={cloneCampaign} />}
    </Modal>
  );
};
